/** Default: 1 golden ticket per $10 USDC staked (matches backend default). */
export const DEFAULT_GOLDEN_TICKET_BOOST_RATE = { tickets: 1, perUsdc: 10 };
export const DEFAULT_GOLDEN_TICKET_MARKET_RATE = { tickets: 1, perUsdc: 10 };

export function normalizeGoldenTicketBoostRate(raw) {
  if (raw && typeof raw === 'object' && Number(raw.perUsdc) > 0) {
    return {
      tickets: Math.max(0, parseInt(raw.tickets, 10) || 0),
      perUsdc: Math.max(0.01, Number(raw.perUsdc) || DEFAULT_GOLDEN_TICKET_BOOST_RATE.perUsdc),
    };
  }
  return { ...DEFAULT_GOLDEN_TICKET_BOOST_RATE };
}

export const normalizeGoldenTicketMarketRate = normalizeGoldenTicketBoostRate;

/** Golden tickets earned for a gross USDC boost stake (floored — never rounded up). */
export function goldenTicketsForBoostStake(stakeUsdc, rate = DEFAULT_GOLDEN_TICKET_BOOST_RATE) {
  const amt = Number(stakeUsdc) || 0;
  const { tickets, perUsdc } = normalizeGoldenTicketBoostRate(rate);
  if (!(amt > 0) || !(perUsdc > 0) || !(tickets > 0)) return 0;
  return Math.max(0, Math.floor((amt / perUsdc) * tickets + 1e-9));
}

export function goldenTicketsForMarketBuy(buyUsdc, rate = DEFAULT_GOLDEN_TICKET_MARKET_RATE) {
  return goldenTicketsForBoostStake(buyUsdc, rate);
}

export function formatGoldenTicketRateLabel(rate = DEFAULT_GOLDEN_TICKET_BOOST_RATE, verb = 'staked') {
  const { tickets, perUsdc } = normalizeGoldenTicketBoostRate(rate);
  const ticketWord = tickets === 1 ? 'golden ticket' : 'golden tickets';
  return `${tickets} ${ticketWord} per each $${perUsdc} USDC ${verb}`;
}

/** Explains floor rounding so users know $18 at $10/ticket still earns 1. */
export function formatGoldenTicketFloorHint(rate = DEFAULT_GOLDEN_TICKET_BOOST_RATE, verb = 'staked') {
  const { tickets, perUsdc } = normalizeGoldenTicketBoostRate(rate);
  if (!(tickets > 0) || !(perUsdc > 0)) return 'Golden tickets are not awarded on this stake.';
  const exampleStake = Number((perUsdc + perUsdc * 0.8).toFixed(2));
  const exampleEarned = goldenTicketsForBoostStake(exampleStake, { tickets, perUsdc });
  const ticketWord = tickets === 1 ? 'golden ticket' : 'golden tickets';
  const earnedWord = exampleEarned === 1 ? 'ticket' : 'tickets';
  return `You earn ${tickets} ${ticketWord} for each full $${perUsdc} ${verb}. Amounts below a full $${perUsdc} do not round up — for example, $${exampleStake} still earns ${exampleEarned} ${earnedWord}.`;
}

export function usdcUntilNextGoldenTicket(stakeUsdc, rate = DEFAULT_GOLDEN_TICKET_BOOST_RATE) {
  const amt = Number(stakeUsdc) || 0;
  const { tickets, perUsdc } = normalizeGoldenTicketBoostRate(rate);
  if (!(tickets > 0) || !(perUsdc > 0) || !(amt > 0)) return null;
  const earned = goldenTicketsForBoostStake(amt, { tickets, perUsdc });
  const nextCount = earned + 1;
  const needed = (nextCount / tickets) * perUsdc;
  const remaining = Math.max(0, needed - amt);
  return { earned, remaining, needed };
}
